import { isSavedRecordingUnavailable } from "@/services/recordingOwnership";

/**
 * Durable, best-effort recovery store for the live recording's audio (SCRIBE-05).
 *
 * `recordingBuffer.ts` holds captured chunks in a module-level JS variable only.
 * That survives a component remount (MDRS-233) but NOT a hard page reload or
 * browser crash — those destroy the JS heap outright, so every chunk vanishes
 * with no error and no salvage. The doctor's assessment is simply gone, and
 * nothing else salvages it: the dictation path's own crash snapshot held the
 * live TEXT transcript only, was never surfaced to anyone, and was removed in
 * MDRS-571 for leaving clinical text in localStorage past logout.
 *
 * This module mirrors each chunk into IndexedDB as it's captured, so a reload
 * mid-recording can still recover the audio and get it transcribed, even though
 * the MediaRecorder/MediaStream themselves cannot survive the reload and the
 * live recording cannot silently resume.
 *
 * `findOrphanedRecording` is looked up per case (not globally) because the
 * recovery prompt itself is case-scoped, to avoid disclosing that "case X's
 * recording was interrupted" to a user who hasn't navigated to case X (a real
 * PHI concern on a shared workstation — see RecordingRecoveryPrompt). That in
 * turn means the store can hold more than one case's orphan at once, so a new
 * session must only ever discard ITS OWN case's prior orphan, never another
 * case's — that other orphan may not have been surfaced to anyone yet.
 *
 * Storage is best-effort: any failure (IndexedDB unavailable, blocked upgrade,
 * private-mode restrictions) is swallowed and must never block the recording
 * itself.
 */
import { SCRIBE_RECOVERY_DB_NAME } from "@constants/live-transcription";

import { getCurrentUserKey } from "@utils/appToken";
import {
  registerRecoveryDisowner,
  registerSavedRecordingCheck,
} from "@utils/clearRecordingRecovery";

import type { SpeakerHint } from "../components/SpeakerChecklist";

const DB_NAME = SCRIBE_RECOVERY_DB_NAME;
const DB_VERSION = 1;
const STORE_NAME = "chunks";
/**
 * A recording nobody ever returns to recover must not hold patient audio in
 * IndexedDB forever. `findOrphanedRecording` purges anything older than this
 * instead of surfacing it.
 */
const MAX_ORPHAN_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type PersistedChunk = {
  id?: number;
  userId?: string | null;
  speakerHints?: SpeakerHint[];
  submissionId?: string;
  sessionId: string;
  caseNumber: string | null;
  examineeName: string | null;
  mimeType: string;
  startedAt: number;
  /**
   * Raw bytes, not a `Blob`. `Blob` is nominally structured-clonable, but not
   * every IndexedDB implementation (real or, notably, `fake-indexeddb` under
   * happy-dom in tests) round-trips one intact — ArrayBuffer is a plain binary
   * type every structured-clone implementation handles correctly.
   */
  data: ArrayBuffer;
};

export type RecoveredRecording = {
  userId?: string | null;
  speakerHints?: SpeakerHint[];
  submissionId?: string;
  sessionId: string;
  caseNumber: string | null;
  examineeName: string | null;
  mimeType: string;
  startedAt: number;
  blob: Blob;
};

/**
 * The session currently allowed to write chunks, held in-memory for the life
 * of this tab only. Mirrors `recordingBuffer.ts`'s ownership guard: a stale
 * write from a session that no longer owns the store must be ignored, not
 * appended into whatever comes next.
 */
let owner: {
  userId?: string | null;
  speakerHints?: SpeakerHint[];
  submissionId?: string;
  sessionId: string;
  caseNumber: string | null;
  examineeName: string | null;
  mimeType: string;
  startedAt: number;
} | null = null;

// A stopped session being released: refuses new writes while ones already past
// the ownership checks land (see `releasePersistedRecording`).
let sealedSessionId: string | null = null;

// Tracked so logout and release can wait for a write already past its ownership checks.
const chunkWrites = new Set<Promise<void>>();

// Logout: stop all writes so the DB delete that follows sticks.
registerRecoveryDisowner(async () => {
  owner = null;
  await Promise.all([...chunkWrites].map((w) => w.catch(() => {})));
});

const hasIndexedDb = () => typeof indexedDB !== "undefined";

const openDb = (): Promise<IDBDatabase | null> =>
  new Promise((resolve) => {
    if (!hasIndexedDb()) {
      resolve(null);
      return;
    }
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

/**
 * Runs one request against the chunk store and resolves with its result once
 * the transaction completes. A transaction (not just the request) is awaited
 * because that's the point at which IndexedDB guarantees the write is durable.
 */
const runRequest = <T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  makeRequest: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T | null> =>
  new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, mode);
      const request = makeRequest(tx.objectStore(STORE_NAME));
      let result: T | null = null;
      request.onsuccess = () => {
        result = request.result;
      };
      request.onerror = () => {
        /* tx.onerror below still fires and resolves */
      };
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

/**
 * Deletes every record matching `predicate` via cursor, leaving the rest —
 * the store has no index to delete by, and record counts are always tiny
 * (a handful of chunks per session), so a full cursor scan is cheap.
 */
const deleteMatching = (
  db: IDBDatabase,
  predicate: (record: PersistedChunk) => boolean
): Promise<void> =>
  new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const cursorRequest = tx.objectStore(STORE_NAME).openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) return;
        if (predicate(cursor.value as PersistedChunk)) cursor.delete();
        cursor.continue();
      };
      cursorRequest.onerror = () => {
        /* tx.onerror below still fires and resolves */
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });

/**
 * Whether any record matches `predicate`, stopping at the first hit so it never
 * loads every saved chunk's audio into memory.
 */
const anyMatching = (
  db: IDBDatabase,
  predicate: (record: PersistedChunk) => boolean
): Promise<boolean> =>
  new Promise((resolve) => {
    try {
      const cursorRequest = db
        .transaction(STORE_NAME, "readonly")
        .objectStore(STORE_NAME)
        .openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) return resolve(false);
        if (predicate(cursor.value as PersistedChunk)) return resolve(true);
        cursor.continue();
      };
      cursorRequest.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });

/**
 * Whether the store still holds audio for ANY case (MDRS-551). Logout deletes
 * the whole store, so it asks this first — e.g. after a refresh, when nothing
 * is in memory. Audio past the retention window doesn't count.
 */
export const hasSavedRecordings = async (): Promise<boolean> => {
  const db = await openDb();
  if (!db) return false;
  try {
    const now = Date.now();
    return await anyMatching(
      db,
      (r) => now - r.startedAt <= MAX_ORPHAN_AGE_MS && r.data?.byteLength > 0
    );
  } finally {
    db.close();
  }
};
registerSavedRecordingCheck(hasSavedRecordings);

/**
 * Claims the persisted store for a new session, discarding only a PRIOR
 * session's leftover audio for this SAME case — mirrors
 * `beginRecordingBuffer`'s "discard anything left behind" rule, but scoped:
 * a different case's still-unsurfaced orphan must survive (see module
 * doc comment), so a doctor starting a fresh recording on Case B can never
 * silently destroy Case A's still-unrecovered audio.
 */
export const beginPersistedRecording = async (meta: {
  userId?: string | null;
  speakerHints?: SpeakerHint[];
  submissionId?: string;
  sessionId: string;
  caseNumber: string | null;
  examineeName: string | null;
  mimeType: string;
}): Promise<void> => {
  owner = { ...meta, userId: getCurrentUserKey(), startedAt: Date.now() };
  const db = await openDb();
  if (!db) return;
  try {
    await deleteMatching(db, (r) => r.caseNumber === meta.caseNumber);
  } finally {
    db.close();
  }
};

/**
 * Appends a chunk. Ignored unless `sessionId` still owns the persisted store.
 *
 * Two `await`s (`chunk.arrayBuffer()`, then `openDb()`) each open a window in
 * which a graceful teardown (`clearPersistedRecording`/a new
 * `beginPersistedRecording`) can run to completion — MediaRecorder can fire a
 * final `dataavailable` synchronously right before `stop`, so this isn't just
 * theoretical. `owner` is therefore re-read (not reused from a stale snapshot)
 * immediately before the write too, so a session that ended mid-write can
 * never leave a record behind for `findOrphanedRecording` to misreport as an
 * interrupted session.
 */
export const persistRecordingChunk = (
  sessionId: string,
  chunk: Blob
): Promise<void> => {
  const write = writeChunk(sessionId, chunk).finally(() =>
    chunkWrites.delete(write)
  );
  chunkWrites.add(write);
  return write;
};

const writeChunk = async (sessionId: string, chunk: Blob): Promise<void> => {
  if (owner?.sessionId !== sessionId || sealedSessionId === sessionId) return;
  if (!chunk || chunk.size === 0) return;
  const data = await chunk.arrayBuffer();
  const claimed = owner;
  if (claimed?.sessionId !== sessionId) return;
  const db = await openDb();
  if (!db) return;
  try {
    // Re-check the LIVE owner, not `claimed` — it may have changed again
    // while `openDb()` was pending.
    if (owner?.sessionId !== sessionId) return;
    const record: PersistedChunk = { ...claimed, data };
    await runRequest(db, "readwrite", (store) => store.add(record));
  } finally {
    db.close();
  }
};

/**
 * Stops writing to a stopped session's audio WITHOUT deleting it (MDRS-551).
 * The copy must outlive Stop until the summarize request is accepted, so a
 * failed upload can still be retried, downloaded, or recovered after a reload.
 * Waits for writes already in flight first — the recorder flushes its final
 * timeslice right before `stop` — so the saved copy keeps the last second.
 */
export const releasePersistedRecording = async (
  sessionId: string
): Promise<void> => {
  if (owner?.sessionId !== sessionId) return;
  sealedSessionId = sessionId;
  await Promise.all([...chunkWrites].map((w) => w.catch(() => {})));
  if (owner?.sessionId === sessionId) owner = null;
  if (sealedSessionId === sessionId) sealedSessionId = null;
};

/**
 * Drops all durably persisted audio for a session. Called once its recording is
 * confirmed sent, on cancel/discard, and on discard-on-error, so only an
 * interrupted or unsent session is ever found by `findOrphanedRecording`.
 * Deletes only that session's own records — another case's orphan sitting in
 * the same store must be left alone.
 */
export const clearPersistedRecording = async (sessionId: string): Promise<void> => {
  if (owner?.sessionId === sessionId) owner = null;
  const db = await openDb();
  if (!db) return;
  try {
    await deleteMatching(db, (r) => r.sessionId === sessionId);
  } finally {
    db.close();
  }
};

/**
 * Reads back an orphaned recording left by a session that never reached a
 * graceful teardown (hard reload/crash mid-recording), scoped to `caseNumber`
 * — the caller (RecordingRecoveryPrompt) is itself case-scoped for PHI-
 * disclosure reasons, and the store can hold more than one case's orphan at
 * once (see module doc comment), so this must never return a different
 * case's audio. Also opportunistically purges any orphan (for ANY case) past
 * the retention window while it's here, since there's no separate sweep.
 * Returns null when nothing was captured worth recovering for this case,
 * IndexedDB is unavailable, or reading fails.
 */
export const findOrphanedRecording = async (
  caseNumber: string | null
): Promise<RecoveredRecording | null> => {
  if (await isSavedRecordingUnavailable()) return null;
  const db = await openDb();
  if (!db) return null;
  try {
    const records = await runRequest(
      db,
      "readonly",
      (store) => store.getAll() as IDBRequest<PersistedChunk[]>
    );
    if (!records || records.length === 0) return null;

    const now = Date.now();
    const staleSessionIds = new Set(
      records
        .filter((r) => now - r.startedAt > MAX_ORPHAN_AGE_MS)
        .map((r) => r.sessionId)
    );
    if (staleSessionIds.size > 0) {
      await deleteMatching(db, (r) => staleSessionIds.has(r.sessionId));
    }

    const forCase = records.filter(
      (r) =>
        r.caseNumber === caseNumber &&
        r.userId === getCurrentUserKey() &&
        !!r.userId &&
        !staleSessionIds.has(r.sessionId)
    );
    if (forCase.length === 0) return null;
    const [{ sessionId, examineeName, mimeType, startedAt }] = forCase;
    const blob = new Blob(
      forCase.filter((r) => r.sessionId === sessionId).map((r) => r.data),
      { type: mimeType }
    );
    // A session that persisted metadata but died before its first timeslice
    // has nothing worth recovering — don't surface an empty "recovered" blob.
    if (blob.size === 0) return null;
    return {
      sessionId,
      submissionId: forCase[0]?.submissionId,
      speakerHints: forCase[0]?.speakerHints,
      caseNumber,
      examineeName,
      mimeType,
      startedAt,
      blob,
    };
  } finally {
    db.close();
  }
};

/** Full in-memory ownership reset — tests only (does not touch IndexedDB). */
export const resetRecordingPersistenceForTest = (): void => {
  owner = null;
  sealedSessionId = null;
};

/** The IndexedDB database name — tests only, so a suite can wipe it between cases. */
export const DB_NAME_FOR_TEST = DB_NAME;

/** The orphan retention window — tests only. */
export const MAX_ORPHAN_AGE_MS_FOR_TEST = MAX_ORPHAN_AGE_MS;
